import { getServiceUrl } from '@airclou/utils';
import { HTTP_TIMEOUT_MS } from '../config';

const API_BASE_URL = getServiceUrl('dashboard');

export interface OverviewStats {
  currentPlan: {
    // Null when the tenant has no plan -- the common case, not an edge one.
    // The card renders a dash rather than naming a plan nobody is on. See
    // docs/overview-unbacked-metrics.md.
    name: string | null;
    tier: string | null;
    // Period end lives in Stripe; the overview endpoint does not call it.
    renewalDate: string | null;
    status: string;
  };
  teamMembers: {
    total: number;
    change: number;
  };
  usage: {
    // A ceiling is a number, 'unlimited' (enterprise and trial), or null when
    // the tenant has no plan to read one from. Those are three different
    // things and the UI renders each differently -- do not collapse them.
    //
    // `used`/`percentage` are null wherever nothing meters the metric. They
    // were Math.random() until the overview honesty pass; only seats is
    // measured today.
    apiCalls: {
      used: number | null;
      // No 'unlimited' here: plan_features has no API quota at all
      // (`api_access` is a boolean), so there is no ceiling concept to express.
      limit: number | null;
      percentage: number | null;
    };
    // Bytes: files + 5 × database (storage-quota-and-fair-use-plan.md §4.7).
    // `used` is null when it could not be counted (band 'unknown'); `limit` is
    // null only when the organization itself could not be read.
    storage: {
      used: number | null;
      /** The two parts of `used`; `messages` is message text and search data as counted. */
      files: number | null;
      messages: number | null;
      limit: { quota: number; ceiling: number } | 'unlimited' | null;
      band: 'under' | 'nearly_full' | 'full' | 'ceiling' | 'unlimited' | 'unknown' | null;
      percentage: number | null;
      trash: number | null;
      nextPurgeAt: string | null;
      /** False until the rule is disclosed (S15): bands show, nothing is refused. */
      enforced: boolean;
    };
    seats: {
      used: number;
      limit: number | 'unlimited' | null;
      percentage: number | null;
    };
  };
  features: {
    total: number;
    active: number;
  };
}

export interface ActivityItem {
  id: string;
  // The audit log's own category vocabulary ('settings', 'workspaces',
  // 'profiles', 'logos', ...). Open-ended by design: ActivityFeed maps the
  // ones it has icons for and falls back to a generic icon for the rest.
  type: string;
  description: string;
  timestamp: string;
  metadata?: Record<string, any>;
}

export interface Alert {
  id: string;
  type: 'warning' | 'error' | 'info';
  category: 'quota' | 'payment' | 'security' | 'action_required';
  title: string;
  description: string;
  actionLabel?: string;
  actionUrl?: string;
  timestamp: string;
}

export interface UsageTrendData {
  date: string;
  apiCalls: number;
  storage: number;
}

/**
 * Fetch overview statistics for the current tenant
 */
export async function fetchOverviewStats(token: string): Promise<OverviewStats> {
  const response = await fetch(`${API_BASE_URL}/api/overview/stats`, {
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    signal: AbortSignal.timeout(HTTP_TIMEOUT_MS),
  });

  if (!response.ok) {
    throw new Error('Failed to fetch overview stats');
  }

  const json = await response.json();
  return json.data;
}

/**
 * Fetch recent activity for the current tenant
 */
export async function fetchRecentActivity(token: string, limit: number = 10): Promise<ActivityItem[]> {
  const response = await fetch(`${API_BASE_URL}/api/overview/activity?limit=${limit}`, {
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    signal: AbortSignal.timeout(HTTP_TIMEOUT_MS),
  });

  if (!response.ok) {
    throw new Error('Failed to fetch recent activity');
  }

  const json = await response.json();
  return json.data || [];
}

/**
 * Fetch alerts and action items for the current tenant
 */
export async function fetchAlerts(token: string): Promise<Alert[]> {
  const response = await fetch(`${API_BASE_URL}/api/overview/alerts`, {
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    signal: AbortSignal.timeout(HTTP_TIMEOUT_MS),
  });

  if (!response.ok) {
    throw new Error('Failed to fetch alerts');
  }

  const json = await response.json();
  return json.data || [];
}

/**
 * Fetch usage trend data for charts
 */
export async function fetchUsageTrends(token: string, days: number = 30): Promise<UsageTrendData[]> {
  const response = await fetch(`${API_BASE_URL}/api/overview/usage-trends?days=${days}`, {
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    signal: AbortSignal.timeout(HTTP_TIMEOUT_MS),
  });

  if (!response.ok) {
    throw new Error('Failed to fetch usage trends');
  }

  const json = await response.json();
  return json.data || [];
}
